import { z } from 'zod';
import { montarCaminho, requisitar } from './client.js';
import type { Pagina, ParametrosDePagina } from './paginacao.js';
import { paginaSchema, parametrosDePagina } from './paginacao.js';
import { analisar } from './validacao.js';

/**
 * Coletas do painel (RF-070, UC03).
 *
 * A tela lista DOCUMENTOS — uma linha por pedido —, e não execuções. Quem
 * agrega é o servidor: a paginação é dele, e somar itens no navegador daria um
 * "8 / 8" que só valeria para a página carregada.
 */

const CAMINHO_COLECAO = montarCaminho('/coletas');

export const origemDoDocumentoSchema = z.enum([
  'PedidoVenda',
  'RequisicaoEletronica',
  'ComponenteOrdemProducao',
]);

export const statusDoDocumentoSchema = z.enum(['Aberto', 'EmColeta', 'Concluido', 'Cancelado']);

export type OrigemDoDocumento = z.infer<typeof origemDoDocumentoSchema>;
export type StatusDoDocumento = z.infer<typeof statusDoDocumentoSchema>;

export const operadorDaColetaSchema = z.object({
  id: z.string(),
  matricula: z.string(),
  nome: z.string(),
});

export const coletaDaListaSchema = z.object({
  id: z.string(),
  /** Referência externa do documento — o número que o operador leu no QR. */
  pedido: z.string(),
  origem: origemDoDocumentoSchema,
  status: statusDoDocumentoSchema,
  /** Denominador do QR (RF-013): quantas linhas o pedido tinha. */
  totalItens: z.number().int().nonnegative(),
  itensConcluidos: z.number().int().nonnegative(),
  comDivergencia: z.boolean(),
  /** Alguma execução aguarda decisão humana (RF-083, RF-098). */
  retidaNaIntegracao: z.boolean(),
  vinculosManuais: z.number().int().nonnegative(),
  /** Horário do APARELHO; nulo enquanto ninguém coletou nada. */
  iniciadaEm: z.string().nullable(),
  encerradaEm: z.string().nullable(),
  operador: operadorDaColetaSchema.nullable(),
  criadoEm: z.string(),
});

export type ColetaDaLista = z.infer<typeof coletaDaListaSchema>;

const paginaDeColetasSchema = paginaSchema(coletaDaListaSchema);

export type FiltroDeColetas = ParametrosDePagina & {
  /** Início do período (ISO). */
  readonly de?: string;
  readonly ate?: string;
  /** Busca PARCIAL pelo número do pedido. */
  readonly referencia?: string;
  readonly operadorId?: string;
};

export async function listarColetas(filtro: FiltroDeColetas = {}): Promise<Pagina<ColetaDaLista>> {
  const corpo = await requisitar(CAMINHO_COLECAO, {
    consulta: {
      ...parametrosDePagina(filtro),
      de: filtro.de,
      ate: filtro.ate,
      referencia: filtro.referencia,
      operadorId: filtro.operadorId,
    },
  });

  return analisar(paginaDeColetasSchema, corpo, CAMINHO_COLECAO);
}

export type SituacaoColeta = 'andamento' | 'concluida' | 'divergencia' | 'cancelada';

/**
 * O selo da linha, e a ordem das perguntas importa.
 *
 * O que pede gente vem primeiro: uma coleta fechada COM divergência, ou com uma
 * execução retida esperando decisão no painel, não é "concluída" — chamá-la
 * assim esconderia exatamente a linha que alguém precisa olhar.
 */
export function situacaoDaColeta(coleta: ColetaDaLista): SituacaoColeta {
  if (coleta.status === 'Cancelado') {
    return 'cancelada';
  }

  if (coleta.comDivergencia || coleta.retidaNaIntegracao) {
    return 'divergencia';
  }

  return coleta.status === 'Concluido' ? 'concluida' : 'andamento';
}

const SIGLA_DA_ORIGEM: Readonly<Record<OrigemDoDocumento, string>> = {
  PedidoVenda: 'PV',
  RequisicaoEletronica: 'RQ',
  ComponenteOrdemProducao: 'OP',
};

/** A sigla que aparece no QR, e não o nome do enum do banco. */
export function rotuloDaOrigem(origem: OrigemDoDocumento): string {
  return SIGLA_DA_ORIGEM[origem];
}

/** De onde partiu a ação — o coletor, o painel, a fila de integração. */
export const origemDoEventoSchema = z.enum(['Coletor', 'Painel', 'Integracao', 'Sistema']);

export type OrigemDoEvento = z.infer<typeof origemDoEventoSchema>;

export const eventoDaColetaSchema = z.object({
  id: z.string(),
  /** Ação do catálogo de auditoria, ou `documento.aberto` (derivada). */
  acao: z.string(),
  origem: origemDoEventoSchema,
  /** Matrícula do operador ou login do painel. */
  autor: z.string().nullable(),
  /**
   * Horário do APARELHO — quando a peça saiu da prateleira.
   *
   * Nulo quando a ação nasceu no painel: ali os dois relógios são o mesmo, e
   * inventar um diria que alguém estava no corredor.
   */
  aconteceuEm: z.string().nullable(),
  /** Quando o middleware gravou. */
  registradoEm: z.string(),
  linha: z.number().int().nullable(),
  sku: z.string().nullable(),
  desfecho: z.string().nullable(),
  codigoLido: z.string().nullable(),
  /** Nula quando a filial não se resolveu — a execução nasce `Retida`. */
  codFil: z.number().int().nullable(),
  /** Campos 5-6 do QR: `codDep` do Senior, na forma canônica. */
  depositoColeta: z.string().nullable(),
  depositoEntrega: z.string().nullable(),
  quantidadeSolicitada: z.string().nullable(),
  quantidadeColetada: z.string().nullable(),
  divergente: z.boolean(),
  naoEncontrado: z.boolean(),
  vinculoManual: z.boolean(),
  statusDaOperacao: z.string().nullable(),
});

export type EventoDaColeta = z.infer<typeof eventoDaColetaSchema>;

export const itemDoPedidoSchema = z.object({
  linha: z.number().int(),
  sku: z.string(),
  derivacao: z.string().nullable(),
  codFil: z.number().int().nullable(),
  depositoColeta: z.string().nullable(),
  depositoEntrega: z.string().nullable(),
  quantidade: z.string(),
  status: z.string(),
  vinculoManual: z.boolean(),
});

export type ItemDoPedido = z.infer<typeof itemDoPedidoSchema>;

export const historicoDaColetaSchema = z.object({
  id: z.string(),
  pedido: z.string(),
  origem: origemDoDocumentoSchema,
  status: statusDoDocumentoSchema,
  totalItens: z.number().int().nonnegative(),
  itensConcluidos: z.number().int().nonnegative(),
  criadoEm: z.string(),
  itens: z.array(itemDoPedidoSchema),
  eventos: z.array(eventoDaColetaSchema),
});

export type HistoricoDaColeta = z.infer<typeof historicoDaColetaSchema>;

/** O backlog de um pedido: tudo que aconteceu dentro dele, com hora e autor. */
export async function consultarHistoricoDaColeta(id: string): Promise<HistoricoDaColeta> {
  const caminho = montarCaminho('/coletas/{id}', { id });

  return analisar(historicoDaColetaSchema, await requisitar(caminho), caminho);
}
